import { useEffect, useLayoutEffect, useRef } from "react";
import { focusTarget } from "@/lib/motion";
import { readerScrollBehavior } from "@/lib/scrollBehavior";

const positions = new Map();
const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export const useRouteScroll = (mainRef, location, navigationType) => {
  const previous = useRef(null);
  useEffect(() => {
    const original = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => { window.history.scrollRestoration = original; };
  }, []);
  useBrowserLayoutEffect(() => {
    const key = `${location.key}:${location.pathname}${location.hash}`;
    const changedPage = previous.current !== location.pathname;
    const first = previous.current === null;
    previous.current = location.pathname;
    const saved = positions.get(key);
    if (navigationType === "POP" && saved) {
      window.scrollTo({ top: saved.y, left: saved.x, behavior: "instant" });
    } else if (location.hash) {
      let id;
      try { id = decodeURIComponent(location.hash.slice(1)); } catch { id = location.hash.slice(1); }
      const target = document.getElementById(id);
      focusTarget(target);
      target?.scrollIntoView({ behavior: changedPage ? "instant" : readerScrollBehavior(), block: "start" });
    } else if (changedPage) {
      window.scrollTo({ top: 0, behavior: "instant" });
      if (!first) mainRef.current?.focus({ preventScroll: true });
    }
    const save = () => {
      positions.set(key, { x: window.scrollX, y: window.scrollY });
      if (positions.size > 120) positions.delete(positions.keys().next().value);
    };
    // Capture before a link commit can shorten the old document and clamp scrollY.
    window.addEventListener("scroll", save, { passive: true });
    document.addEventListener("click", save, true);
    window.addEventListener("pagehide", save);
    return () => {
      window.removeEventListener("scroll", save);
      document.removeEventListener("click", save, true);
      window.removeEventListener("pagehide", save);
    };
  }, [location.pathname, location.hash, location.key, navigationType, mainRef]);
};